/**
 * Portfolio Page - Detailed Holdings Dashboard
 * Shows all owned assets with purchase history and trends
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { alpacaAPI, botAPI } from '../services/api';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import {
  TrendingUp, TrendingDown, DollarSign, Activity, BarChart3,
  RefreshCw, PieChart, Layers, ArrowUpRight, ArrowDownRight,
  Clock, Calendar, Filter, Search, ChevronDown, ChevronUp,
  Briefcase, Coins, FileText, Bitcoin, Shield, Target, Crosshair
} from 'lucide-react';

// Asset type icons and colors
const ASSET_TYPES = {
  stock: { label: 'Stocks', icon: BarChart3, color: 'blue' },
  etf: { label: 'ETFs', icon: Layers, color: 'purple' },
  crypto: { label: 'Crypto', icon: Bitcoin, color: 'orange' },
  option: { label: 'Options', icon: FileText, color: 'green' },
};

// Mini trend chart component with hover tooltip
const TrendChart = ({ data, color = 'blue', height = 64 }) => {
  const [hoverIndex, setHoverIndex] = React.useState(null);
  const svgRef = React.useRef(null);

  if (!data || data.length < 2) return null;
  
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  
  // Add 5% padding to top and bottom
  const paddedMin = min - range * 0.05;
  const paddedRange = range * 1.1;
  
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - ((val - paddedMin) / paddedRange) * 90 - 5;
    return `${x},${y}`;
  }).join(' ');
  
  // Area fill points (close the path at the bottom)
  const areaPoints = points + ` 100,100 0,100`;
  
  const colorMap = {
    green: '#10b981',
    red: '#ef4444',
    blue: '#3b82f6',
    purple: '#8b5cf6',
    orange: '#f59e0b',
  };
  
  const strokeColor = colorMap[color] || colorMap.blue;
  const gradId = `grad-${color}-${Math.random().toString(36).substr(2, 5)}`;

  const handleMouseMove = (e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const idx = Math.round(x * (data.length - 1));
    setHoverIndex(Math.max(0, Math.min(idx, data.length - 1)));
  };

  const hoverX = hoverIndex !== null ? (hoverIndex / (data.length - 1)) * 100 : null;
  const hoverY = hoverIndex !== null ? 100 - ((data[hoverIndex] - paddedMin) / paddedRange) * 90 - 5 : null;
  
  return (
    <div className="relative" style={{ height }} 
      onMouseLeave={() => setHoverIndex(null)}
    >
      <svg 
        ref={svgRef}
        viewBox="0 0 100 100" 
        preserveAspectRatio="none" 
        style={{ height, width: '100%' }}
        onMouseMove={handleMouseMove}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={strokeColor} stopOpacity="0.3" />
            <stop offset="100%" stopColor={strokeColor} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <polygon points={areaPoints} fill={`url(#${gradId})`} />
        <polyline
          points={points}
          fill="none"
          stroke={strokeColor}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {hoverIndex !== null && (
          <>
            <line x1={hoverX} y1="0" x2={hoverX} y2="100" stroke="#ffffff" strokeWidth="0.5" strokeDasharray="2,2" opacity="0.4" />
            <circle cx={hoverX} cy={hoverY} r="2.5" fill={strokeColor} stroke="#fff" strokeWidth="1" />
          </>
        )}
      </svg>
      {hoverIndex !== null && (
        <div 
          className="absolute top-0 pointer-events-none bg-bg-primary border border-border-color rounded px-2 py-1 shadow-lg z-10"
          style={{ 
            left: `${(hoverIndex / (data.length - 1)) * 100}%`,
            transform: hoverIndex > data.length * 0.7 ? 'translateX(-100%)' : 'translateX(-50%)',
          }}
        >
          <div className="font-mono text-xs font-bold">${data[hoverIndex].toFixed(2)}</div>
          <div className="text-[9px] text-text-muted">{data.length - 1 - hoverIndex}D ago</div>
        </div>
      )}
    </div>
  );
};

// Stop level visual bar component
const StopLevelBar = ({ stopData, currentPrice, avgCost }) => {
  if (!stopData || !stopData.stopPrice) return null;
  
  const { stopPrice, target1, target3, breakevenActive } = stopData;
  
  // Calculate bar range: stop to target3 (or stop to current + some buffer)
  const rangeMin = Math.min(stopPrice, currentPrice * 0.95);
  const rangeMax = target3 || currentPrice * 1.1;
  const range = rangeMax - rangeMin || 1;
  
  const stopPct = ((stopPrice - rangeMin) / range) * 100;
  const entryPct = ((avgCost - rangeMin) / range) * 100;
  const currentPct = ((currentPrice - rangeMin) / range) * 100;
  const t1Pct = target1 ? ((target1 - rangeMin) / range) * 100 : null;
  const t3Pct = target3 ? ((target3 - rangeMin) / range) * 100 : null;

  const clamp = (v) => Math.max(2, Math.min(98, v));
  
  return (
    <div className="mt-3">
      <div className="flex justify-between text-xs text-text-muted mb-1">
        <span>Stop ${stopPrice.toFixed(0)}</span>
        <span>T3 ${target3?.toFixed(0) || '—'}</span>
      </div>
      <div className="relative h-2 bg-bg-tertiary rounded-full overflow-visible">
        {/* Stop zone (red) */}
        <div className="absolute h-full bg-accent-red/30 rounded-l-full" style={{ left: 0, width: `${clamp(stopPct)}%` }} />
        {/* Profit zone (green) */}
        {t1Pct && <div className="absolute h-full bg-accent-green/20 rounded-r-full" style={{ left: `${clamp(entryPct)}%`, width: `${Math.max(0, clamp(t3Pct || currentPct) - clamp(entryPct))}%` }} />}
        
        {/* Stop marker */}
        <div className="absolute w-1.5 h-3.5 bg-accent-red rounded-sm -top-0.5" style={{ left: `${clamp(stopPct)}%` }} title={`Stop: $${stopPrice.toFixed(2)}`} />
        
        {/* Entry marker */}
        <div className="absolute w-1 h-3 bg-text-muted rounded-sm -top-0.5 opacity-50" style={{ left: `${clamp(entryPct)}%` }} title={`Entry: $${avgCost.toFixed(2)}`} />
        
        {/* Target 1 marker */}
        {t1Pct && <div className="absolute w-1.5 h-3.5 bg-accent-blue rounded-sm -top-0.5" style={{ left: `${clamp(t1Pct)}%` }} title={`T1: $${target1.toFixed(2)}`} />}
        
        {/* Current price dot */}
        <div className="absolute w-2.5 h-2.5 bg-white border-2 border-accent-blue rounded-full -top-0.5" style={{ left: `${clamp(currentPct)}%`, transform: 'translateX(-50%)' }} title={`Current: $${currentPrice.toFixed(2)}`} />
      </div>
    </div>
  );
};

// Holdings card component
const HoldingCard = ({ holding, onSelect, isSelected, stopData }) => {
  const isProfit = holding.unrealizedPL >= 0;
  const trendColor = isProfit ? 'green' : 'red';
  
  return (
    <div 
      onClick={() => onSelect(holding)}
      className={`card p-5 cursor-pointer transition-all hover:border-accent-blue ${isSelected ? 'border-accent-blue ring-2 ring-accent-blue/20' : ''}`}
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-lg font-bold">{holding.symbol}</span>
            <span className={`badge badge-${ASSET_TYPES[holding.assetType]?.color || 'blue'}`}>
              {holding.assetType?.toUpperCase() || 'STOCK'}
            </span>
            {stopData?.strategy && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-bg-tertiary text-text-muted uppercase">
                {stopData.strategy}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-text-muted text-sm">{holding.name || holding.symbol}</p>
            {stopData?.breakevenActive && (
              <span className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-accent-blue/10 text-accent-blue">
                <Shield className="w-3 h-3" /> BE
              </span>
            )}
            {stopData?.partialTaken && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-accent-green/10 text-accent-green">½</span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-xl font-bold">${holding.marketValue?.toLocaleString()}</div>
          <div className={`flex items-center justify-end gap-1 text-sm ${isProfit ? 'text-accent-green' : 'text-accent-red'}`}>
            {isProfit ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            <span>{isProfit ? '+' : ''}{holding.unrealizedPLPercent?.toFixed(2)}%</span>
          </div>
        </div>
      </div>
      
      {/* Mini trend chart with labels */}
      <div className="mb-4">
        {holding.priceHistory?.length >= 2 ? (
          <div className="flex gap-1">
            <div className="flex-1 h-16">
              <TrendChart data={holding.priceHistory} color={trendColor} height={64} />
            </div>
            <div className="flex flex-col justify-between text-right w-12 py-0.5">
              <span className="text-[10px] font-mono text-text-muted">
                ${Math.max(...holding.priceHistory).toFixed(0)}
              </span>
              <span className="text-[10px] font-mono text-text-muted">
                ${Math.min(...holding.priceHistory).toFixed(0)}
              </span>
            </div>
          </div>
        ) : (
          <div className="h-16 flex items-center justify-center text-text-muted text-xs">
            No chart data
          </div>
        )}
        <div className="flex justify-between mt-0.5 px-0.5">
          <span className="text-[10px] text-text-muted">{holding.priceHistory?.length || 0}D ago</span>
          <span className="text-[10px] text-text-muted">Today</span>
        </div>
      </div>
      
      {/* Key stats */}
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div className="bg-bg-tertiary rounded-lg p-2 text-center">
          <div className="text-text-muted text-xs">Qty</div>
          <div className="font-mono font-semibold">{holding.qty}</div>
        </div>
        <div className="bg-bg-tertiary rounded-lg p-2 text-center">
          <div className="text-text-muted text-xs">Avg Cost</div>
          <div className="font-mono font-semibold">${holding.avgCost?.toFixed(2)}</div>
        </div>
        <div className="bg-bg-tertiary rounded-lg p-2 text-center">
          <div className="text-text-muted text-xs">{stopData?.stopPrice ? 'Stop' : 'Current'}</div>
          <div className={`font-mono font-semibold ${stopData?.stopPrice ? 'text-accent-red' : ''}`}>
            ${stopData?.stopPrice?.toFixed(2) || holding.currentPrice?.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Stop level bar */}
      <StopLevelBar 
        stopData={stopData} 
        currentPrice={holding.currentPrice} 
        avgCost={holding.avgCost} 
      />
      
      {/* Buy/Sell activity indicator */}
      {holding.recentActivity && (
        <div className={`mt-3 flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg ${
          holding.recentActivity === 'buy' ? 'bg-accent-green/10 text-accent-green' : 'bg-accent-red/10 text-accent-red'
        }`}>
          {holding.recentActivity === 'buy' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          <span>Recent {holding.recentActivity === 'buy' ? 'Buy' : 'Sell'} activity</span>
        </div>
      )}
    </div>
  );
};

// Detailed holding panel
const HoldingDetailPanel = ({ holding, onClose, trades, stopData }) => {
  if (!holding) return null;
  
  const isProfit = holding.unrealizedPL >= 0;
  
  // Calculate buy/sell stats
  const buyTrades = trades.filter(t => t.side === 'buy');
  const sellTrades = trades.filter(t => t.side === 'sell');
  const avgBuyPrice = buyTrades.length > 0 
    ? buyTrades.reduce((sum, t) => sum + t.price, 0) / buyTrades.length 
    : 0;
  const lowestBuy = buyTrades.length > 0 
    ? Math.min(...buyTrades.map(t => t.price)) 
    : 0;
  const highestBuy = buyTrades.length > 0 
    ? Math.max(...buyTrades.map(t => t.price)) 
    : 0;
  const lastBuyPrice = buyTrades.length > 0 
    ? buyTrades[0]?.price 
    : 0;
  
  return (
    <div className="card p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold flex items-center gap-3">
            {holding.symbol}
            <span className={`badge badge-${ASSET_TYPES[holding.assetType]?.color || 'blue'}`}>
              {holding.assetType?.toUpperCase() || 'STOCK'}
            </span>
          </h2>
          <p className="text-text-secondary">{holding.name}</p>
        </div>
        <button onClick={onClose} className="btn-secondary p-2">
          <ChevronDown className="w-5 h-5" />
        </button>
      </div>

      {/* Phase 3: Profit Protection Status */}
      {stopData && stopData.stopPrice && (
        <div className="mb-6 bg-bg-tertiary rounded-xl p-4 border border-border-color">
          <h3 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-accent-blue" /> Profit Protection
            {stopData.breakevenActive && (
              <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-accent-blue/15 text-accent-blue font-medium">🛡️ Breakeven Active</span>
            )}
            {stopData.partialTaken && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-accent-green/15 text-accent-green font-medium">½ Partial Taken</span>
            )}
          </h3>
          
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="bg-bg-primary rounded-lg p-3">
              <div className="text-text-muted text-xs mb-1">ATR Stop</div>
              <div className="font-mono font-bold text-lg text-accent-red">${stopData.stopPrice.toFixed(2)}</div>
              <div className="text-text-muted text-xs">
                {stopData.atr ? `ATR $${stopData.atr.toFixed(2)} × ${stopData.stopMultiplier}` : ''}
              </div>
            </div>
            <div className="bg-bg-primary rounded-lg p-3">
              <div className="text-text-muted text-xs mb-1">High Water Mark</div>
              <div className="font-mono font-bold text-lg text-accent-blue">${stopData.highWaterMark?.toFixed(2)}</div>
              <div className="text-text-muted text-xs">Peak since entry</div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-bg-primary rounded-lg p-3">
              <div className="flex items-center gap-1 text-text-muted text-xs mb-1">
                <Target className="w-3 h-3" /> Target 1 (1× ATR)
              </div>
              <div className={`font-mono font-bold text-lg ${holding.currentPrice >= (stopData.target1 || Infinity) ? 'text-accent-green' : 'text-text-primary'}`}>
                ${stopData.target1?.toFixed(2) || '—'}
              </div>
              <div className="text-text-muted text-xs">Partial profit + BE stop</div>
            </div>
            <div className="bg-bg-primary rounded-lg p-3">
              <div className="flex items-center gap-1 text-text-muted text-xs mb-1">
                <Crosshair className="w-3 h-3" /> Target 3 (3× ATR)
              </div>
              <div className="font-mono font-bold text-lg">${stopData.target3?.toFixed(2) || '—'}</div>
              <div className="text-text-muted text-xs">Full position exit</div>
            </div>
          </div>

          {/* Stop level visual */}
          <StopLevelBar stopData={stopData} currentPrice={holding.currentPrice} avgCost={holding.avgCost} />
          
          {/* Distance to stop */}
          {stopData.stopPrice && (
            <div className="mt-3 flex justify-between text-xs text-text-muted">
              <span>Distance to stop: <span className="text-accent-red font-mono">
                {((holding.currentPrice - stopData.stopPrice) / holding.currentPrice * 100).toFixed(1)}%
              </span></span>
              {stopData.target1 && (
                <span>Distance to T1: <span className="text-accent-green font-mono">
                  {((stopData.target1 - holding.currentPrice) / holding.currentPrice * 100).toFixed(1)}%
                </span></span>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Value summary */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-bg-tertiary rounded-xl p-4">
          <div className="text-text-muted text-sm mb-1">Total Value</div>
          <div className="font-mono text-3xl font-bold">${holding.marketValue?.toLocaleString()}</div>
          <div className="text-text-muted text-sm mt-1">{holding.qty} shares</div>
        </div>
        <div className={`rounded-xl p-4 ${isProfit ? 'bg-accent-green/10' : 'bg-accent-red/10'}`}>
          <div className="text-text-muted text-sm mb-1">Unrealized P&L</div>
          <div className={`font-mono text-3xl font-bold ${isProfit ? 'text-accent-green' : 'text-accent-red'}`}>
            {isProfit ? '+' : ''}${holding.unrealizedPL?.toLocaleString()}
          </div>
          <div className={`text-sm mt-1 ${isProfit ? 'text-accent-green' : 'text-accent-red'}`}>
            {isProfit ? '+' : ''}{holding.unrealizedPLPercent?.toFixed(2)}%
          </div>
        </div>
      </div>
      
      {/* Price stats */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
          <DollarSign className="w-4 h-4" /> Price Analysis
        </h3>
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-bg-tertiary rounded-lg p-3 text-center">
            <div className="text-text-muted text-xs mb-1">Current Price</div>
            <div className="font-mono font-bold text-lg">${holding.currentPrice?.toFixed(2)}</div>
          </div>
          <div className="bg-bg-tertiary rounded-lg p-3 text-center">
            <div className="text-text-muted text-xs mb-1">Avg Buy Price</div>
            <div className="font-mono font-bold text-lg text-accent-blue">${avgBuyPrice.toFixed(2)}</div>
          </div>
          <div className="bg-bg-tertiary rounded-lg p-3 text-center">
            <div className="text-text-muted text-xs mb-1">Lowest Buy</div>
            <div className="font-mono font-bold text-lg text-accent-green">${lowestBuy.toFixed(2)}</div>
          </div>
          <div className="bg-bg-tertiary rounded-lg p-3 text-center">
            <div className="text-text-muted text-xs mb-1">Highest Buy</div>
            <div className="font-mono font-bold text-lg text-accent-red">${highestBuy.toFixed(2)}</div>
          </div>
        </div>
      </div>
      
      {/* Buy/Sell Trend */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4" /> Trading Activity
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-accent-green/10 border border-accent-green/20 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <ArrowUpRight className="w-5 h-5 text-accent-green" />
              <span className="font-semibold text-accent-green">Buying Trend</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-text-muted">Total Buys</span>
                <span className="font-mono">{buyTrades.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-text-muted">Last Buy</span>
                <span className="font-mono">${lastBuyPrice.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-text-muted">Avg Buy</span>
                <span className="font-mono">${avgBuyPrice.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <div className="bg-accent-red/10 border border-accent-red/20 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <ArrowDownRight className="w-5 h-5 text-accent-red" />
              <span className="font-semibold text-accent-red">Selling Trend</span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-text-muted">Total Sells</span>
                <span className="font-mono">{sellTrades.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-text-muted">Last Sell</span>
                <span className="font-mono">${sellTrades[0]?.price?.toFixed(2) || '-'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-text-muted">Avg Sell</span>
                <span className="font-mono">
                  ${sellTrades.length > 0 
                    ? (sellTrades.reduce((s, t) => s + t.price, 0) / sellTrades.length).toFixed(2) 
                    : '-'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Recent trades */}
      <div>
        <h3 className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4" /> Recent Transactions
        </h3>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {trades.slice(0, 10).map((trade, idx) => (
            <div 
              key={idx}
              className={`flex items-center justify-between p-3 rounded-lg ${
                trade.side === 'buy' ? 'bg-accent-green/5' : 'bg-accent-red/5'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  trade.side === 'buy' ? 'bg-accent-green/15' : 'bg-accent-red/15'
                }`}>
                  {trade.side === 'buy' 
                    ? <ArrowUpRight className="w-4 h-4 text-accent-green" />
                    : <ArrowDownRight className="w-4 h-4 text-accent-red" />
                  }
                </div>
                <div>
                  <div className="font-semibold text-sm">{trade.side.toUpperCase()}</div>
                  <div className="text-text-muted text-xs">{trade.qty} shares @ ${trade.price?.toFixed(2)}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-sm">${(trade.qty * trade.price).toFixed(2)}</div>
                <div className="text-text-muted text-xs">
                  {new Date(trade.timestamp).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
          {trades.length === 0 && (
            <div className="text-center py-4 text-text-muted">No transactions recorded</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Portfolio summary component
const PortfolioSummary = ({ holdings, accountData }) => {
  const totalValue = holdings.reduce((sum, h) => sum + (h.marketValue || 0), 0);
  const totalPL = holdings.reduce((sum, h) => sum + (h.unrealizedPL || 0), 0);
  const totalCost = holdings.reduce((sum, h) => sum + (h.costBasis || 0), 0);
  
  // Group by asset type
  const byType = holdings.reduce((acc, h) => {
    const type = h.assetType || 'stock';
    if (!acc[type]) acc[type] = { count: 0, value: 0 };
    acc[type].count++;
    acc[type].value += h.marketValue || 0;
    return acc;
  }, {});
  
  return (
    <div className="card p-6 mb-6">
      <h2 className="font-display text-xl font-semibold mb-4 flex items-center gap-2">
        <PieChart className="w-5 h-5 text-accent-purple" />
        Portfolio Summary
      </h2>
      
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-bg-tertiary rounded-xl p-4">
          <div className="text-text-muted text-sm mb-1">Total Holdings</div>
          <div className="font-mono text-2xl font-bold">${totalValue.toLocaleString()}</div>
        </div>
        <div className="bg-bg-tertiary rounded-xl p-4">
          <div className="text-text-muted text-sm mb-1">Total Cost Basis</div>
          <div className="font-mono text-2xl font-bold">${totalCost.toLocaleString()}</div>
        </div>
        <div className={`rounded-xl p-4 ${totalPL >= 0 ? 'bg-accent-green/10' : 'bg-accent-red/10'}`}>
          <div className="text-text-muted text-sm mb-1">Total P&L</div>
          <div className={`font-mono text-2xl font-bold ${totalPL >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
            {totalPL >= 0 ? '+' : ''}${totalPL.toLocaleString()}
          </div>
        </div>
        <div className="bg-bg-tertiary rounded-xl p-4">
          <div className="text-text-muted text-sm mb-1">Positions</div>
          <div className="font-mono text-2xl font-bold">{holdings.length}</div>
        </div>
      </div>
      
      {/* Asset allocation */}
      <div className="flex gap-4">
        {Object.entries(byType).map(([type, data]) => {
          const config = ASSET_TYPES[type] || ASSET_TYPES.stock;
          const Icon = config.icon;
          const percentage = totalValue > 0 ? ((data.value / totalValue) * 100).toFixed(1) : 0;
          
          return (
            <div key={type} className={`flex-1 bg-accent-${config.color}/10 border border-accent-${config.color}/20 rounded-xl p-4`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 text-accent-${config.color}`} />
                <span className={`text-sm font-medium text-accent-${config.color}`}>{config.label}</span>
              </div>
              <div className="font-mono text-xl font-bold">${data.value.toLocaleString()}</div>
              <div className="text-text-muted text-sm">{data.count} positions • {percentage}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Main Portfolio Page
const Portfolio = () => {
  const { hasAlpacaKeys } = useAuth();
  const [holdings, setHoldings] = useState([]);
  const [accountData, setAccountData] = useState(null);
  const [tradeHistory, setTradeHistory] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('value');
  const [trailingStops, setTrailingStops] = useState({});
  
  const fetchData = useCallback(async () => {
    if (!hasAlpacaKeys) {
      setLoading(false);
      return;
    }
    
    try {
      const [positionsRes, accountRes, historyRes, trailingRes] = await Promise.all([
        alpacaAPI.getPositions().catch(() => ({ data: [] })),
        alpacaAPI.getAccount().catch(() => null),
        alpacaAPI.getOrders?.().catch(() => ({ data: [] })),
        botAPI.getTrailingStops().catch(() => ({ data: {} })),
      ]);
      
      // Store trailing stop data
      setTrailingStops(trailingRes?.data || {});
      
      // Process positions into holdings with extra data
      const processedHoldings = (positionsRes.data || []).map(pos => {
        const currentPrice = pos.currentPrice || 0;
        const avgCost = pos.avgCost || 0;
        
        return {
          symbol: pos.symbol,
          name: pos.symbol,
          assetType: pos.asset_class === 'crypto' ? 'crypto' : 'stock',
          qty: Math.abs(pos.qty || 0),
          avgCost: avgCost,
          currentPrice: currentPrice,
          marketValue: pos.marketValue || 0,
          costBasis: pos.costBasis || 0,
          unrealizedPL: pos.pnl || 0,
          unrealizedPLPercent: pos.pnlPercent || 0,
          dayPnl: pos.dayPnl || 0,
          dayPnlPercent: pos.dayPnlPercent || 0,
          priceHistory: [], // Will be filled by batch bars
          recentActivity: null,
        };
      });
      
      setHoldings(processedHoldings);
      if (accountRes?.data) setAccountData(accountRes.data);

      // Fetch real price history for mini charts (chunked to avoid API limits)
      if (processedHoldings.length > 0) {
        try {
          const allSymbols = processedHoldings.map(h => h.symbol);
          const barsData = {};
          
          // Chunk into groups of 10
          for (let i = 0; i < allSymbols.length; i += 10) {
            const chunk = allSymbols.slice(i, i + 10).join(',');
            const barsRes = await alpacaAPI.getBarsBatch(chunk);
            Object.assign(barsData, barsRes.data || {});
          }
          
          setHoldings(prev => prev.map(h => ({
            ...h,
            priceHistory: barsData[h.symbol] || [],
          })));
        } catch (barsErr) {
          console.error('Failed to fetch chart data:', barsErr);
        }
      }

      // Fetch real trade history from journal
      try {
        const journalRes = await botAPI.getJournal({ limit: 200 });
        const journalEntries = journalRes.data?.entries || [];
        const realHistory = {};
        journalEntries.forEach(entry => {
          if (!realHistory[entry.symbol]) realHistory[entry.symbol] = [];
          realHistory[entry.symbol].push({
            side: entry.side,
            qty: entry.qty,
            price: entry.price,
            timestamp: entry.timestamp,
          });
        });
        setTradeHistory(realHistory);
      } catch (journalErr) {
        console.error('Failed to fetch trade history:', journalErr);
        setTradeHistory({});
      }
      
    } catch (err) {
      console.error('Failed to fetch portfolio data:', err);
      toast.error('Failed to load portfolio');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [hasAlpacaKeys]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };
  
  // Filter and sort holdings
  const filteredHoldings = holdings
    .filter(h => {
      if (filterType !== 'all' && h.assetType !== filterType) return false;
      if (searchQuery && !h.symbol.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'value': return b.marketValue - a.marketValue;
        case 'pl': return b.unrealizedPL - a.unrealizedPL;
        case 'plPercent': return b.unrealizedPLPercent - a.unrealizedPLPercent;
        case 'symbol': return a.symbol.localeCompare(b.symbol);
        default: return 0;
      }
    });
  
  if (!hasAlpacaKeys) {
    return (
      <div className="min-h-screen p-6">
        <div className="card p-12 text-center">
          <Briefcase className="w-16 h-16 text-accent-blue mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Connect Your Broker</h2>
          <p className="text-text-secondary mb-6">Link your Alpaca account to view your portfolio holdings.</p>
          <Link to="/" className="btn-primary">Go to Dashboard</Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen">
      <Navigation 
        title="Portfolio Holdings" 
        subtitle="Detailed view of all your investments"
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />
      
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        {/* Portfolio Summary */}
        {!loading && holdings.length > 0 && (
          <PortfolioSummary holdings={holdings} accountData={accountData} />
        )}
      
      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="flex items-center gap-2 px-4 py-2 bg-bg-tertiary rounded-xl flex-1 max-w-xs">
            <Search className="w-5 h-5 text-text-muted" />
            <input
              type="text"
              placeholder="Search holdings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-text-primary placeholder:text-text-muted w-full"
            />
          </div>
          
          {/* Type filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-text-muted" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="bg-bg-tertiary border border-border-color rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">All Types</option>
              <option value="stock">Stocks</option>
              <option value="etf">ETFs</option>
              <option value="crypto">Crypto</option>
              <option value="option">Options</option>
            </select>
          </div>
          
          {/* Sort */}
          <div className="flex items-center gap-2">
            <span className="text-text-muted text-sm">Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-bg-tertiary border border-border-color rounded-lg px-3 py-2 text-sm"
            >
              <option value="value">Market Value</option>
              <option value="pl">P&L ($)</option>
              <option value="plPercent">P&L (%)</option>
              <option value="symbol">Symbol</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Holdings Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="card p-5">
              <div className="h-32 loading-shimmer rounded" />
            </div>
          ))}
        </div>
      ) : filteredHoldings.length === 0 ? (
        <div className="card p-12 text-center">
          <Briefcase className="w-16 h-16 text-text-muted mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">No Holdings Found</h2>
          <p className="text-text-secondary">
            {holdings.length === 0 
              ? "You don't have any positions yet. Start trading to build your portfolio!"
              : "No holdings match your search criteria."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Holdings list */}
          <div className={`${selectedHolding ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredHoldings.map(holding => (
                <HoldingCard
                  key={holding.symbol}
                  holding={holding}
                  onSelect={setSelectedHolding}
                  isSelected={selectedHolding?.symbol === holding.symbol}
                  stopData={trailingStops[holding.symbol]}
                />
              ))}
            </div>
          </div>
          
          {/* Detail panel */}
          {selectedHolding && (
            <div className="lg:col-span-1">
              <div className="sticky top-6">
                <HoldingDetailPanel
                  holding={selectedHolding}
                  onClose={() => setSelectedHolding(null)}
                  trades={tradeHistory[selectedHolding.symbol] || []}
                  stopData={trailingStops[selectedHolding.symbol]}
                />
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default Portfolio;
